import {
  createMascotInventoryItem,
  deleteMascotInventoryItem,
  patchMascotInventoryItem,
} from "../requests/GamificationRequests";

// The slots the mascot wears — the same ones Profile → Maskotum lets the learner fill.
// BANNER also lives in the mascot inventory but is the profile background, applied from its own
// picker, so buying never touches it.
export const MASCOT_SLOT_TYPE_LIST = [
  "SHIRT",
  "EYE",
  "JACKET",
  "HEAD",
  "NECK",
  "GLASSES",
  "VOICE",
  "ACT",
];

export type MascotSlotChange =
  | { kind: "CREATE"; type: string; marketItemId: number }
  | {
      kind: "PATCH";
      type: string;
      mascotInventoryItemId: number;
      marketItemId: number;
    }
  | { kind: "DELETE"; type: string; mascotInventoryItemId: number };

/**
 * What it takes to make the saved mascot wear `targetMarketItemList`, one change per slot in
 * `slotTypeList`: an empty slot gets a CREATE, a slot holding another item a PATCH, and a filled
 * slot the target leaves empty a DELETE. Slots outside `slotTypeList` are never touched.
 */
export const planMascotSlotChangeList = (
  savedInventoryItemList: any[],
  targetMarketItemList: any[],
  slotTypeList: string[]
): MascotSlotChange[] => {
  const changeList: MascotSlotChange[] = [];
  slotTypeList.forEach((type) => {
    const saved = savedInventoryItemList.find(
      (item) => item.marketItemDTO?.type === type
    );
    const target = targetMarketItemList.find((item) => item.type === type);
    if (target && !saved) {
      changeList.push({ kind: "CREATE", type, marketItemId: target.id });
    } else if (target && saved && saved.marketItemDTO.id !== target.id) {
      changeList.push({
        kind: "PATCH",
        type,
        mascotInventoryItemId: saved.id,
        marketItemId: target.id,
      });
    } else if (!target && saved) {
      changeList.push({
        kind: "DELETE",
        type,
        mascotInventoryItemId: saved.id,
      });
    }
  });
  return changeList;
};

/**
 * Runs the planned changes through the same equip requests Profile → Maskotum uses and returns the
 * resulting saved inventory. A slot whose request fails keeps its old saved value and is reported
 * in `failedTypeList`, so the caller never shows an outfit that was not stored.
 */
export const applyMascotSlotChangeList = async (
  savedInventoryItemList: any[],
  changeList: MascotSlotChange[],
  userId: number,
  userProfileId: number
): Promise<{ inventoryItemList: any[]; failedTypeList: string[] }> => {
  let inventoryItemList = [...savedInventoryItemList];
  const failedTypeList: string[] = [];
  for (const change of changeList) {
    if (change.kind === "CREATE") {
      const { data, err } = await createMascotInventoryItem({
        marketItem: { id: change.marketItemId },
        userId,
        userProfileId,
      });
      if (err) {
        failedTypeList.push(change.type);
        continue;
      }
      inventoryItemList.push(data);
    } else if (change.kind === "PATCH") {
      const { data, err } = await patchMascotInventoryItem({
        id: change.mascotInventoryItemId,
        type: change.type,
        marketItemId: change.marketItemId,
      });
      if (err) {
        failedTypeList.push(change.type);
        continue;
      }
      inventoryItemList = inventoryItemList.map((item) =>
        item.id === change.mascotInventoryItemId
          ? { ...item, marketItemDTO: (data as any).marketItemDTO }
          : item
      );
    } else {
      const { err } = await deleteMascotInventoryItem(
        change.mascotInventoryItemId
      );
      if (err) {
        failedTypeList.push(change.type);
        continue;
      }
      inventoryItemList = inventoryItemList.filter(
        (item) => item.id !== change.mascotInventoryItemId
      );
    }
  }
  return { inventoryItemList, failedTypeList };
};
